import { useEffect, useState } from "react";
import { Button, Stack, TextField, Typography } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { UNLOCK_PIN } from "common-fe";

// #144/#629 — the everyday wake-from-standby PIN. Was hardcoded to "1234";
// #629's approved design shares ONE per-show PIN with ScribeLock rather than
// a separate resume PIN, so the caller (InitialSetup.jsx) now passes
// `pin={getScribePin(selectedShow)}` — this default only covers the
// standalone/unwired case (e.g. tests rendering ScribeResume directly).
export default function ScribeResume({ onResume, pin: expectedPin = UNLOCK_PIN }) {
  const [active, setActive] = useState(false);
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);

  // Auto-submit once entered length matches the expected PIN's length
  // (per-show PINs aren't necessarily 4 digits, mirrors common-fe's PinLock).
  useEffect(() => {
    if (!active) return undefined;
    if (pin.length !== expectedPin.length) return undefined;
    const id = setTimeout(() => {
      if (pin === expectedPin) {
        setActive(false);
        setPin("");
        onResume?.();
      } else {
        setError(true);
        setTimeout(() => {
          setPin("");
          setError(false);
        }, 600);
      }
    }, 80);
    return () => clearTimeout(id);
  }, [pin, active, expectedPin, onResume]);

  if (!active) {
    return (
      <Button
        data-testid="resume-button"
        variant="contained"
        size="large"
        fullWidth
        startIcon={<PlayArrowIcon />}
        onClick={() => setActive(true)}
        sx={{ py: 1.5, fontWeight: 600 }}
      >
        Resume
      </Button>
    );
  }

  return (
    <Stack spacing={1.5} alignItems="stretch">
      <Typography variant="caption" color="text.secondary" align="center">
        Enter your {expectedPin.length}-digit PIN
      </Typography>
      <TextField
        autoFocus
        type="tel"
        value={pin}
        onChange={(e) => {
          // Numeric only, capped at the expected PIN's length.
          const next = e.target.value.replace(/\D/g, "").slice(0, expectedPin.length);
          setPin(next);
          if (error) setError(false);
        }}
        error={error}
        helperText={error ? "Incorrect PIN" : " "}
        placeholder={"•".repeat(expectedPin.length)}
        inputProps={{
          "data-testid": "resume-pin-input",
          inputMode: "numeric",
          pattern: "[0-9]*",
          maxLength: expectedPin.length,
          autoComplete: "off",
          style: {
            textAlign: "center",
            fontSize: 28,
            letterSpacing: 12,
            fontFamily: "monospace",
            padding: "12px 0",
          },
        }}
        fullWidth
      />
      <Button
        variant="text"
        // #629 — noticed while screenshotting this screen for the per-show
        // PIN change: default text-Button color resolves to primary.main,
        // which renders dim against the dark background (same class of bug
        // as PinLock.jsx's own Cancel, #180) — match that fix.
        color="inherit"
        onClick={() => {
          setActive(false);
          setPin("");
          setError(false);
        }}
      >
        Cancel
      </Button>
    </Stack>
  );
}
